import { useQuery } from "@tanstack/react-query";
import { Send, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { fetchOutreachCandidateById, initiateOutreachChat } from "../../behavior/api/outreach";
import type { ApiError } from "../../behavior/api/outreach";
import { Button, Heading, Text, Alert } from "../../components/elements";
import { useElevenLabsChat } from "../../hooks/useElevenLabsChat";
import TransitionPage from "../transition/TransitionPage";
import OutreachHeader from "./components/OutreachHeader";
import OutreachFooter from "./components/OutreachFooter";
import "./OutreachChatPage.css";

const OutreachChatPage = () => {
    const { candidateId } = useParams<{ candidateId: string }>();
    const { t, i18n } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const [userOverrideLanguage, setUserOverrideLanguage] = useState<string | undefined>();
    const [chatError, setChatError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const { messages, status, sendMessage, connect, isAgentTyping } = useElevenLabsChat();

    const {
        data: candidate,
        isLoading: candidateLoading,
        error,
    } = useQuery({
        queryKey: ["outreachCandidate", candidateId],
        queryFn: fetchOutreachCandidateById(candidateId || ""),
        enabled: !!candidateId,
    });

    const currentUiLangCode = i18n.language.split("-")[0];
    const isUiLangAvailable = !!candidate?.campaign.languages.includes(currentUiLangCode);

    const activeLanguage =
        userOverrideLanguage || (isUiLangAvailable ? currentUiLangCode : candidate?.candidate.language) || "en";

    const handleStartChat = async () => {
        if (!candidateId || !candidate) return;
        setChatError(null);
        try {
            const res = await initiateOutreachChat(candidateId);
            connect(res.token, {
                ...res.dynamicVariables,
                language: activeLanguage,
            });
        } catch (err: unknown) {
            console.error("Failed to start chat:", err);
            const error = err as ApiError;
            if (error.status === 403) {
                setChatError(error.message || t("Outreach.Chat_ForbiddenError"));
            } else {
                setChatError(t("Outreach.Chat_GeneralError"));
            }
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const getLanguageLabel = (code: string) => {
        const labels: Record<string, string> = {
            en: "English",
            sv: "Svenska",
            da: "Dansk",
            de: "Deutsch",
            nl: "Nederlands",
            pt: "Português",
            tr: "Türkçe",
            no: "Norsk",
            fi: "Suomi",
            fr: "Français",
            el: "Ελληνικά",
            pl: "Polski",
            ja: "日本語",
            cs: "Čeština",
            hu: "Magyar",
            sk: "Slovenčina",
            uk: "Українська",
        };
        return labels[code] || code;
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isAgentTyping]);

    const userInitials = candidate ? `${candidate.candidate.firstName?.[0] || ""}`.toUpperCase() : "AA";

    const handleSend = () => {
        if (!inputValue.trim()) return;
        sendMessage(inputValue);
        setInputValue("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSend();
        }
    };

    if (candidateLoading) {
        return (
            <TransitionPage
                title={t("TransitionPage.loading-title")}
                subtitle={t("Outreach.LoadingCandidateDetails")}
            />
        );
    }

    if (error || !candidate) {
        return (
            <div className="outreach-chat-page">
                <div className="outreach-chat-container">
                    <div className="messages-area">
                        <div className="empty-state">
                            <Heading level="h3">{t("Outreach.CandidateNotFound")}</Heading>
                            <Text>{t("Outreach.CandidateNotFoundDesc")}</Text>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const companyName = candidate.campaign.companyName || candidate.campaign.customerName;

    return (
        <div className="outreach-chat-page">
            <div className="outreach-chat-container">
                <OutreachHeader companyName={companyName} status={status} />

                <div className="messages-area">
                    {(status === "idle" || status === "disconnected") && messages.length === 0 ? (
                        <div className="empty-state">
                            {!candidate.actions.canChatInterview || chatError ? (
                                <div className="forbidden-state">
                                    <Alert type="warning" className="forbidden-alert">
                                        <div className="forbidden-content">
                                            <Heading level="h4">{t("Outreach.Chat_ForbiddenError")}</Heading>
                                            <Text size="small">{chatError || t("Outreach.InterviewEndedDesc2")}</Text>
                                        </div>
                                    </Alert>
                                </div>
                            ) : (
                                <>
                                    <Text className="start-prompt">{t("Outreach.Chat_ReadyToStart")}</Text>

                                    {candidate.campaign.languages.length > 1 && (
                                        <div className="language-selection-container">
                                            <div className="language-selection-wrapper">
                                                <Text size="small" className="select-label">
                                                    {t("Outreach.Chat_SelectLanguage")}
                                                </Text>
                                                <div className="select-input-group">
                                                    <select
                                                        value={activeLanguage}
                                                        onChange={(e) => {
                                                            setUserOverrideLanguage(e.target.value);
                                                        }}
                                                        className="chat-language-select"
                                                    >
                                                        {candidate.campaign.languages.map((lang) => (
                                                            <option key={lang} value={lang}>
                                                                {getLanguageLabel(lang)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <ChevronDown size={16} className="select-icon" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <Button onClick={handleStartChat} className="start-chat-button">
                                        {t("Outreach.Chat_ButtonStart")}
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {messages.length === 0 && (
                                <div className="empty-state">
                                    <Text>{t("Outreach.Chat_SayHello")}</Text>
                                </div>
                            )}

                            {messages.map((msg) => (
                                <div key={msg.id} className={`message-row ${msg.role}`}>
                                    <div className="avatar">{msg.role === "agent" ? "H" : userInitials}</div>
                                    <div className="message-bubble-wrapper">
                                        <div className="message-bubble">
                                            <div className="message-content">
                                                <Text>{msg.text}</Text>
                                            </div>
                                        </div>
                                        <Text size="small" className="message-time">
                                            {new Date(msg.timestamp).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: false,
                                            })}
                                        </Text>
                                    </div>
                                </div>
                            ))}
                            {isAgentTyping && (
                                <div className="message-row agent">
                                    <div className="avatar">H</div>
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                <div className="chat-footer-controls">
                    <div className="input-area">
                        <div className="input-wrapper pill-design">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t("Outreach.Chat_Placeholder")}
                                disabled={status !== "connected"}
                                className="chat-input"
                            />
                            <button
                                className="send-button circle-design"
                                onClick={handleSend}
                                disabled={!inputValue.trim() || status !== "connected"}
                            >
                                <Send size={20} />
                            </button>
                        </div>
                    </div>

                    <OutreachFooter companyName={companyName} privacyPolicies={candidate.legal.privacyPolicies} />
                </div>
            </div>
        </div>
    );
};

export default OutreachChatPage;
